import { toMinutes } from './recipeTime';

/**
 * Validates a URL to ensure it's a valid recipe website URL
 */
export function validateRecipeUrl(url) {
  try {
    const urlObj = new URL(url);
    
    // Check if it's HTTP or HTTPS
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return { valid: false, error: 'URL must use HTTP or HTTPS protocol' };
    }
    
    // Basic check that it's not a local URL
    if (urlObj.hostname === 'localhost' || urlObj.hostname === '127.0.0.1') {
      return { valid: false, error: 'Cannot import from localhost' };
    }
    
    return { valid: true };
  } catch (error) {
    return { valid: false, error: 'Invalid URL format' };
  }
}

/**
 * Transform parsed recipe data into the app's Recipe format
 */
function transformParsedRecipe(parsed, userId) {
  const vulgarFractions = {
    '¼': '1/4',
    '½': '1/2',
    '¾': '3/4',
    '⅓': '1/3',
    '⅔': '2/3',
    '⅛': '1/8',
    '⅜': '3/8',
    '⅝': '5/8',
    '⅞': '7/8',
  };

  const normalizeQtyToken = (token) => {
    if (!token) return '';
    // Insert a space before vulgar fractions when glued to a whole number, then map to ascii fractions
    const spaced = token.replace(/([0-9])([¼½¾⅓⅔⅛⅜⅝⅞])/g, '$1 $2');
    const replaced = spaced.split('').map((ch) => vulgarFractions[ch] || ch).join('');
    return replaced.trim().replace(/\s+/g, ' ');
  };

  const parseQuantityAndUnit = (raw) => {
    if (typeof raw !== 'string') return { quantity: '', name: '' };
    const line = raw.trim();
    if (!line) return { quantity: '', name: '' };

    // Capture leading quantity (whole, mixed numeric, mixed unicode fraction, unicode fraction, decimal), optional unit, remainder as name
    // Groups: 1=qty, 2=unit, 3=name
    // The (?![a-z]) guard stops a unit matching the start of a longer word — without it the
    // bare "g" swallows "grated" ("2 grated carrots" -> 2 g "rated carrots") and "l" swallows
    // "large". Single-letter units are listed last so longer forms are preferred when reading.
    const re = /^\s*((?:\d+\s+\d+\/\d+)|(?:\d+\s+[¼½¾⅓⅔⅛⅜⅝⅞])|(?:\d+[¼½¾⅓⅔⅛⅜⅝⅞])|(?:\d+\/\d+)|(?:\d+\.\d+)|(?:\d+)|[¼½¾⅓⅔⅛⅜⅝⅞])\s*(?:(tablespoons?|tbsps?|tbs|teaspoons?|tsps?|cups?|kilograms?|kgs?|grammes?|grams?|gms?|millilitres?|milliliters?|mls?|litres?|liters?|ounces?|oz|pounds?|lbs?|pinch(?:es)?|dash(?:es)?|cloves?|cans?|packets?|packages?|sticks?|g|l)(?![a-z])\s*)?(.*)$/i;
    const match = line.match(re);
    if (!match) return { quantity: '', name: line };

    const qtyToken = normalizeQtyToken(match[1]);
    const unitToken = match[2] ? match[2].trim() : '';
    const rest = match[3] ? match[3].trim() : '';
    const quantity = [qtyToken, unitToken].filter(Boolean).join(' ').trim();

    // If no remainder, fall back to full line as name
    const name = rest || line;
    return { quantity, name };
  };

  return {
    id: `recipe-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name: parsed.title,
    image: parsed.image || '',
    servings: parsed.servings || 4,
    // Not saved: tells the review form to show "We couldn't find a serving size..." at Servings.
    servingsDefaulted: !parsed.servings,
    prepTime: toMinutes(parsed.prepTime),
    cookTime: toMinutes(parsed.cookTime),
    totalTime: toMinutes(parsed.totalTime),
    ingredients: parsed.ingredients.map((ing, idx) => {
      if (typeof ing === 'object' && ing !== null) {
        return {
          id: ing.id || `ing-${idx}`,
          quantity: ing.quantity || '',
          name: ing.name || ing.ingredient || '',
          unit: ing.unit || '',
          category: ing.category,
        };
      }

      const { quantity, name } = parseQuantityAndUnit(String(ing || ''));
      return {
        id: `ing-${idx}`,
        quantity,
        name,
      };
    }),
    method: parsed.instructions,
    tags: parsed.tags || [],
    mealType: parsed.category?.toLowerCase() || '',
    nutrition: parsed.nutrition,
    source: parsed.sourceUrl || 'Imported Recipe',
    userId,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Parse raw recipe text into structured data
 * This is a client-side parser that works for most common recipe formats
 */
export async function parseRecipeText(text, userId, useMockParser = true) {
  if (useMockParser) {
    return mockParseRecipeText(text, userId);
  }

  // Real API implementation
  try {
    const response = await fetch('/api/recipes/parse-text', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text, userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.message || 'Failed to parse recipe text'
      };
    }

    const data = await response.json();
    return {
      success: true,
      recipe: data.recipe
    };
  } catch (error) {
    console.error('Recipe text parsing error:', error);
    return {
      success: false,
      error: 'Network error occurred while parsing recipe'
    };
  }
}

/**
 * Mock text parser - uses heuristics to parse recipe text
 */
async function mockParseRecipeText(text, userId) {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1500));

  try {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    if (lines.length < 3) {
      return {
        success: false,
        error: 'Recipe text is too short. Please include recipe name, ingredients, and instructions.'
      };
    }

    // Extract recipe title (usually first non-empty line or largest text)
    let title = lines[0];
    
    // Look for common title indicators
    const titleMatch = text.match(/^(.+?)(?:\n|Ingredients|INGREDIENTS)/i);
    if (titleMatch && titleMatch[1].trim().length > 0 && titleMatch[1].trim().length < 100) {
      title = titleMatch[1].trim();
    }

    // Extract servings
    // null = not found in the text; transformParsedRecipe falls back to 4 and flags it.
    let servings = null;
    const servingsMatch = text.match(/(?:serves?|servings?|yield)[\s:]*(\d+)/i);
    if (servingsMatch) {
      servings = parseInt(servingsMatch[1], 10) || null;
    }

    // Extract prep/cook time. Capture the whole duration phrase and let toMinutes work out
    // the units — the previous version hardcoded "min", so "Prep time: 1 hour" matched
    // nothing and the value was silently dropped.
    // Units are enumerated rather than open-ended ([a-z]*) so the capture can't run on into
    // the next line of pasted text; "15 minutes 2 large onions" stops after "15 minutes".
    const UNIT = '(?:hours?|hrs?|h|minutes?|mins?|m)';
    const matchDuration = (label) => {
      const match = text.match(
        new RegExp(`${label}(?:\\s+time)?[\\s:]*((?:\\d+(?:\\.\\d+)?\\s*${UNIT}\\s*){1,2}|\\d+)`, 'i')
      );
      return match ? toMinutes(match[1]) : null;
    };
    const prepTime = matchDuration('prep');
    const cookTime = matchDuration('cook');
    const totalTime = matchDuration('total');

    // Extract ingredients
    const ingredients = [];
    let inIngredientsSection = false;
    let inInstructionsSection = false;
    
    const ingredientMarkers = /^(ingredients?|what you need)[\s:]*$/i;
    const instructionMarkers = /^(instructions?|directions?|method|steps?|how to make)[\s:]*$/i;
    const listItemPattern = /^[-•*\d.)(]+\s+(.+)$/;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check for section headers
      if (ingredientMarkers.test(line)) {
        inIngredientsSection = true;
        inInstructionsSection = false;
        continue;
      }
      
      if (instructionMarkers.test(line)) {
        inInstructionsSection = true;
        inIngredientsSection = false;
        continue;
      }

      // Parse ingredients
      if (inIngredientsSection && !inInstructionsSection) {
        const match = line.match(listItemPattern);
        if (match) {
          // If the line starts with a number, treat it as part of the ingredient (quantity) instead of stripping it
          const startsWithDigit = /^\s*\d/.test(line);
          ingredients.push(startsWithDigit ? line.trim() : match[1].trim());
        } else if (line.length > 3 && !ingredientMarkers.test(line)) {
          // Line without bullet/number but likely an ingredient
          ingredients.push(line);
        }
      }
    }

    // Extract instructions
    const instructions = [];
    inIngredientsSection = false;
    inInstructionsSection = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (instructionMarkers.test(line)) {
        inInstructionsSection = true;
        inIngredientsSection = false;
        continue;
      }
      
      if (ingredientMarkers.test(line)) {
        inIngredientsSection = true;
        inInstructionsSection = false;
        continue;
      }

      if (inInstructionsSection && !inIngredientsSection) {
        const match = line.match(listItemPattern);
        if (match) {
          instructions.push(match[1].trim());
        } else if (line.length > 10 && !instructionMarkers.test(line)) {
          // Line without number but likely an instruction
          instructions.push(line);
        }
      }
    }

    // Fallback: if no sections detected, try to auto-detect
    if (ingredients.length === 0 || instructions.length === 0) {
      let foundIngredientsStart = false;
      let tempIngredients = [];
      let tempInstructions = [];

      for (let i = 1; i < lines.length; i++) { // Start from 1 to skip title
        const line = lines[i];
        const match = line.match(listItemPattern);
        
        if (match) {
          const cleaned = match[1].trim();
          
          // Heuristic: ingredients usually have measurements, instructions are longer sentences
          const hasQuantity = /^\d|cup|tbsp|tsp|oz|lb|gram|kg|ml|pinch|dash/.test(cleaned.toLowerCase());
          const isLongSentence = cleaned.length > 40;
          
          if (hasQuantity && !isLongSentence && !foundIngredientsStart) {
            tempIngredients.push(cleaned);
          } else if (isLongSentence || foundIngredientsStart) {
            foundIngredientsStart = true;
            tempInstructions.push(cleaned);
          }
        }
      }

      if (ingredients.length === 0 && tempIngredients.length > 0) {
        ingredients.push(...tempIngredients);
      }
      if (instructions.length === 0 && tempInstructions.length > 0) {
        instructions.push(...tempInstructions);
      }
    }

    // Validate we got the essential parts
    if (ingredients.length === 0) {
      return {
        success: false,
        error: 'Could not detect ingredients. Please make sure your text includes an ingredients list.'
      };
    }

    if (instructions.length === 0) {
      return {
        success: false,
        error: 'Could not detect instructions. Please make sure your text includes cooking steps.'
      };
    }

    // Auto-detect tags based on content
    const tags = [];
    const textLower = text.toLowerCase();
    
    if (textLower.includes('vegetarian') || (!textLower.includes('meat') && !textLower.includes('chicken') && !textLower.includes('beef'))) {
      tags.push('Vegetarian');
    }
    if (textLower.includes('vegan')) {
      tags.push('Vegan');
    }
    if (textLower.includes('gluten free') || textLower.includes('gluten-free')) {
      tags.push('GF');
    }
    // Removed demo tag injection to avoid default "Dessert"/"Healthy" tags

    const parsedRecipe = {
      title,
      description: `Imported recipe: ${title}`,
      ingredients,
      instructions,
      servings,
      prepTime,
      cookTime,
      totalTime,
      tags,
      // Deliberately blank: this parser cannot infer a meal type, and hardcoding one meant every
      // text import silently claimed 'lunch'. Blank leaves the chips unselected so the mandatory
      // meal-type validation in ManualRecipeForm forces a real choice. Do not re-add a default.
      category: '',
      sourceUrl: 'Text Import',
    };

    const recipe = transformParsedRecipe(parsedRecipe, userId);

    return {
      success: true,
      recipe
    };
  } catch (error) {
    console.error('Error parsing recipe text:', error);
    return {
      success: false,
      error: 'Failed to parse recipe text. Please check the format and try again.'
    };
  }
}

/**
 * Mock function for testing - simulates backend response
 */
export async function mockUploadRecipeByUrl(url, userId) {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  // Simulate random success/failure for testing
  const shouldSucceed = Math.random() > 0.2; // 80% success rate
  
  if (!shouldSucceed) {
    return {
      success: false,
      error: 'Could not parse recipe from this URL. Please make sure it\'s a valid recipe page.'
    };
  }
  
  // Mock parsed recipe data with more realistic variety
  const mockRecipes = [
    {
      title: 'Classic Chocolate Chip Cookies',
      description: 'The best chocolate chip cookies you\'ll ever taste - crispy on the outside, soft on the inside',
      image: 'https://images.unsplash.com/photo-1499636136210-6f4ee915583e?w=800&h=600&fit=crop',
      prepTime: 15,
      cookTime: 12,
      servings: 24,
      ingredients: [
        '2 cups all-purpose flour',
        '1 tsp baking soda',
        '1/2 tsp salt',
        '1 cup butter, softened',
        '3/4 cup granulated sugar',
        '3/4 cup brown sugar',
        '2 large eggs',
        '2 tsp vanilla extract',
        '2 cups chocolate chips'
      ],
      instructions: [
        'Preheat oven to 375°F (190°C).',
        'In a medium bowl, whisk together flour, baking soda, and salt.',
        'In a large bowl, cream butter and both sugars until fluffy (about 3 minutes).',
        'Beat in eggs one at a time, then add vanilla extract.',
        'Gradually stir in flour mixture until just combined.',
        'Fold in chocolate chips.',
        'Drop rounded tablespoons of dough onto ungreased cookie sheets, spacing 2 inches apart.',
        'Bake for 9-11 minutes until edges are golden brown.',
        'Cool on baking sheet for 2 minutes before transferring to wire rack.'
      ],
      tags: ['Dessert', 'Cookies', 'Baking'],
      category: 'Dessert',
      nutrition: {
        calories: 150,
        protein: 2,
        carbs: 20,
        fat: 7
      },
      keywords: ['cookie', 'chocolate', 'dessert', 'baking', 'sweet']
    },
    {
      title: 'Mediterranean Quinoa Bowl',
      description: 'A healthy and delicious Mediterranean-inspired quinoa bowl with fresh vegetables and feta',
      image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&h=600&fit=crop',
      prepTime: 10,
      cookTime: 20,
      servings: 4,
      ingredients: [
        '1 cup quinoa, rinsed',
        '2 cups vegetable broth',
        '1 cucumber, diced',
        '2 cups cherry tomatoes, halved',
        '1 red bell pepper, diced',
        '1/2 red onion, thinly sliced',
        '1 cup feta cheese, crumbled',
        '1/2 cup kalamata olives, pitted and halved',
        '1/4 cup fresh parsley, chopped',
        '3 tbsp olive oil',
        '2 tbsp lemon juice',
        '1 tsp dried oregano',
        'Salt and pepper to taste'
      ],
      instructions: [
        'In a medium saucepan, bring quinoa and vegetable broth to a boil.',
        'Reduce heat to low, cover, and simmer for 15 minutes until liquid is absorbed.',
        'Remove from heat and let stand covered for 5 minutes, then fluff with a fork.',
        'While quinoa cooks, prepare the vegetables.',
        'In a large bowl, combine cucumber, tomatoes, bell pepper, and red onion.',
        'In a small bowl, whisk together olive oil, lemon juice, oregano, salt, and pepper.',
        'Add cooled quinoa to the vegetable mixture.',
        'Pour dressing over the bowl and toss to combine.',
        'Top with feta cheese, olives, and fresh parsley.',
        'Serve immediately or refrigerate for up to 3 days.'
      ],
      tags: ['Vegetarian', 'Healthy', 'Mediterranean', 'GF'],
      category: 'Lunch',
      nutrition: {
        calories: 320,
        protein: 12,
        carbs: 35,
        fat: 16
      },
      keywords: ['quinoa', 'bowl', 'mediterranean', 'salad', 'healthy', 'vegetarian']
    },
    {
      title: 'Garlic Herb Roasted Chicken',
      description: 'Juicy roasted chicken with crispy skin, infused with garlic and fresh herbs',
      image: 'https://images.unsplash.com/photo-1598103442097-8b74394b95c6?w=800&h=600&fit=crop',
      prepTime: 15,
      cookTime: 60,
      servings: 6,
      ingredients: [
        '1 whole chicken (about 4 lbs)',
        '4 tbsp butter, softened',
        '6 cloves garlic, minced',
        '2 tbsp fresh rosemary, chopped',
        '2 tbsp fresh thyme, chopped',
        '1 tbsp fresh parsley, chopped',
        '1 lemon, quartered',
        '2 tsp salt',
        '1 tsp black pepper',
        '1 tsp paprika'
      ],
      instructions: [
        'Preheat oven to 425°F (220°C).',
        'Pat chicken dry with paper towels and place in a roasting pan.',
        'In a small bowl, mix butter, garlic, rosemary, thyme, and parsley.',
        'Gently loosen the skin from the chicken breast and thighs.',
        'Spread half the herb butter under the skin.',
        'Rub remaining butter all over the outside of the chicken.',
        'Season generously with salt, pepper, and paprika.',
        'Place lemon quarters inside the chicken cavity.',
        'Roast for 60-75 minutes until internal temperature reaches 165°F.',
        'Let rest for 10 minutes before carving.'
      ],
      tags: ['Dinner', 'Chicken', 'GF'],
      category: 'Dinner',
      nutrition: {
        calories: 425,
        protein: 38,
        carbs: 2,
        fat: 28
      },
      keywords: ['chicken', 'roasted', 'roast', 'poultry', 'garlic', 'herb']
    },
    {
      title: 'Creamy Cauliflower Cheese Pasta',
      description: 'A delicious pasta dish with tender cauliflower in a rich, cheesy sauce',
      image: 'https://images.unsplash.com/photo-1621996346565-e3dbc646d9a9?w=800&h=600&fit=crop',
      prepTime: 10,
      cookTime: 25,
      servings: 4,
      ingredients: [
        '400g pasta (penne or rigatoni)',
        '1 medium cauliflower, cut into florets',
        '2 tbsp butter',
        '2 tbsp all-purpose flour',
        '2 cups whole milk',
        '2 cups cheddar cheese, grated',
        '1/2 cup parmesan cheese, grated',
        '1 tsp Dijon mustard',
        '1/4 tsp nutmeg',
        'Salt and pepper to taste',
        '2 cloves garlic, minced',
        'Fresh parsley for garnish',
        'Breadcrumbs for topping (optional)'
      ],
      instructions: [
        'Bring a large pot of salted water to a boil.',
        'Add pasta and cauliflower florets, cook together for 10-12 minutes until both are tender.',
        'Reserve 1 cup of pasta water, then drain pasta and cauliflower.',
        'In the same pot, melt butter over medium heat.',
        'Add garlic and cook for 30 seconds until fragrant.',
        'Stir in flour and cook for 1 minute to make a roux.',
        'Gradually whisk in milk, stirring constantly until smooth.',
        'Bring to a simmer and cook for 3-4 minutes until thickened.',
        'Remove from heat and stir in cheddar, parmesan, mustard, and nutmeg.',
        'Add pasta and cauliflower back to the pot, toss to coat.',
        'If sauce is too thick, add reserved pasta water to thin.',
        'Season with salt and pepper, garnish with parsley and serve.'
      ],
      tags: ['Vegetarian', 'Comfort Food', 'Pasta'],
      category: 'Dinner',
      nutrition: {
        calories: 520,
        protein: 24,
        carbs: 58,
        fat: 22
      },
      keywords: ['pasta', 'cauliflower', 'cheese', 'creamy', 'vegetarian', 'comfort']
    }
  ];
  
  // Try to intelligently match the URL to a recipe
  const urlLower = url.toLowerCase();
  let selectedRecipe;
  
  // Check URL for keywords to match appropriate recipe
  if (urlLower.includes('cookie') || urlLower.includes('chocolate') || urlLower.includes('dessert')) {
    selectedRecipe = mockRecipes[0]; // Cookies
  } else if (urlLower.includes('quinoa') || urlLower.includes('bowl') || urlLower.includes('salad')) {
    selectedRecipe = mockRecipes[1]; // Quinoa bowl
  } else if (urlLower.includes('chicken') || urlLower.includes('roast')) {
    selectedRecipe = mockRecipes[2]; // Roasted chicken
  } else if (urlLower.includes('pasta') || urlLower.includes('cauliflower') || urlLower.includes('cheese')) {
    selectedRecipe = mockRecipes[3]; // Cauliflower pasta
  } else {
    // If no match, randomly select
    selectedRecipe = mockRecipes[Math.floor(Math.random() * mockRecipes.length)];
  }
  
  const mockParsedRecipe = {
    ...selectedRecipe,
    sourceUrl: url
  };
  
  const recipe = transformParsedRecipe(mockParsedRecipe, userId);
  
  return {
    success: true,
    recipe
  };
}
